import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { NotificationParams, NotificationWithPage, ResponseData } from '@app/interfaces/lm/notification';
import { environment } from '@env/environment';
import { lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    private moduleApi = environment.api.services.sys;

    private baseUrl = this.moduleApi + '/notices';


    notifications = signal<any[]>([]);
    private shownIds = new Set<number>();

    private pollingTimer: any = null;
    private pollingActive = false;

    private pollingStartTime = 0;
    private maxPollingDurationMs = 5 * 60 * 1000;

    constructor(private http: HttpClient) { }

    async loadInitial(userId: number): Promise<void> {
        const res = await this.find({ page: 0, size: 10, toUserId: userId });
        this.notifications.set(res.content);
        res.content.forEach(n => this.shownIds.add(n.id));
    }

    async pollNew(userId: number): Promise<void> {
        if (!this.pollingActive || userId === null) return;

        const latestId = this.notifications()[0]?.id ?? 0;
        const res = await this.find({ page: 0, size: 10, toUserId: userId });
        if (res.content.length === 0) return;

        const newOnes = res.content.filter(n => n.id > latestId);
        if (newOnes.length > 0) {
            this.notifications.update(old => [...newOnes, ...old]);
            this.stopPolling();
        }
    }

    startPolling(userId: number) {
        if (this.pollingActive) return;
        this.pollingActive = true;
        this.pollingStartTime = Date.now();

        const poll = async () => {
            if (!this.pollingActive) return;

            await this.pollNew(userId);

            const now = Date.now();
            const elapsed = now - this.pollingStartTime;

            const latest = this.notifications()[0];
            const hasNewUnread = latest && !this.shownIds.has(latest.id);

            if (hasNewUnread || elapsed >= this.maxPollingDurationMs) {
                this.stopPolling();
                return;
            }

            this.pollingTimer = setTimeout(poll, 5000);
        };

        poll();
    }

    stopPolling() {
        this.pollingActive = false;
        this.pollingStartTime = 0;

        if (this.pollingTimer) {
            clearTimeout(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    markAsShown(id: number) {
        this.shownIds.add(id);
    }

    isShown(id: number): boolean {
        return this.shownIds.has(id);
    }

    async find(params: NotificationParams): Promise<NotificationWithPage> {
        return lastValueFrom(this.http.get<NotificationWithPage>(this.baseUrl, {
            params: Object.assign(params),
        }));
    }

    async batchRead(data: {
        ids: number[];
    }): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/batch/readed`, data));
    }

    async response(data: ResponseData): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/response`, data));
    }

}
